<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon :color="worstSeverity === 'ok' ? 'success' : worstSeverity">
          {{ worstSeverity === 'ok' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        <span class="px-2">{{ $t('readiness') }}</span>
        <info-tool-tip class="ml-2" :message="$t('readiness_hint')"></info-tool-tip>
        <v-spacer></v-spacer>
        <small v-if="generatedAt" class="text-muted me-3">{{ generatedAt | moment("LT") }}</small>
        <v-btn outlined color="secondary" :loading="isLoading" @click="load">
          <v-icon small class="me-1">mdi-refresh</v-icon>
          {{ $t('refresh') }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <vue-element-loading :active="isLoading" />

        <v-alert v-if="!isLoading && allClear" type="success" text prominent>
          {{ $t('everything_is_ready') }}
        </v-alert>

        <!-- the coin balance in days rather than in coins: "412" means nothing
             on its own, "6 days" is a decision -->
        <v-alert
          v-if="coins && coins.days_left !== null"
          :type="coinSeverity"
          text
          dense
          class="mb-4"
        >
          {{ $t('coins_last_days', {
            days: coins.days_left,
            balance: coins.balance,
            riders: coins.riders,
          }) }}
        </v-alert>

        <v-row>
          <v-col
            v-for="check in sortedChecks"
            :key="check.key"
            cols="12"
            md="6"
          >
            <v-card outlined :class="check.count ? 'readiness-card--' + check.severity : ''">
              <v-card-title class="py-3">
                <v-icon :color="colorFor(check.severity)" class="me-2">
                  {{ iconFor(check.severity) }}
                </v-icon>
                <span class="text-subtitle-1">{{ $t('check_' + check.key) }}</span>
                <v-spacer></v-spacer>
                <v-chip small dark :color="colorFor(check.severity)">{{ check.count }}</v-chip>
              </v-card-title>

              <v-card-text v-if="check.count" class="pt-0">
                <div class="mb-2 text-caption">{{ $t('fix_' + check.key) }}</div>
                <v-chip
                  v-for="item in check.items"
                  :key="check.key + '-' + item.id"
                  class="ma-1"
                  small
                  outlined
                  @click="open(check.key, item)"
                >
                  {{ item.label }}
                  <span v-if="item.detail" class="ms-1 text-caption">({{ item.detail }})</span>
                </v-chip>
                <div v-if="check.truncated" class="mt-2 text-caption">
                  {{ $t('and_n_more', { count: check.truncated }) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
/*
 * What will go wrong tomorrow morning.
 *
 * Every one of these was already knowable and none of it was anywhere: a route
 * with no trip, a trip with no driver, a bus carrying more children than it has
 * seats, a child with no stop, a licence that expired last week, a balance
 * about to run out. Each lived on a different screen, none of those screens
 * said anything was wrong, and the school found out when a bus did not arrive.
 *
 * Deliberately not cached: this is the one screen whose whole value is being
 * current.
 */
import VueElementLoading from "vue-element-loading";
import InfoToolTip from "@/components/InfoToolTip";

//where a row on each check leads
const DESTINATIONS = {
  routes_without_trips: (item) => ({ name: "view-route", params: { route_id: item.id } }),
  trips_without_driver: () => ({ name: "trips" }),
  trips_without_bus: () => ({ name: "trips" }),
  trips_over_capacity: (item) => ({ name: "view-trip", params: { trip_id: item.id } }),
  students_without_stop: (item) => ({ name: "view-student", params: { user_id: item.id } }),
  waiting_for_approval: (item) => ({ name: "view-student", params: { user_id: item.id } }),
  driver_documents_expiring: (item) => ({ name: "view-driver", params: { user_id: item.id } }),
  buses_without_driver: () => ({ name: "buses" }),
  runs_not_started: () => ({ name: "planned-trips" }),
};

export default {
  components: { VueElementLoading, InfoToolTip },
  data() {
    return {
      checks: [],
      coins: null,
      generatedAt: null,
      isLoading: false,
    };
  },
  computed: {
    //the things that are wrong first, worst first, and the clear ones last
    sortedChecks() {
      const weight = { error: 0, warning: 1, info: 2, ok: 3 };
      return this.checks
        .slice()
        .sort((a, b) => weight[a.severity] - weight[b.severity] || b.count - a.count);
    },
    allClear() {
      return this.checks.length > 0 && this.checks.every((check) => check.count === 0);
    },
    worstSeverity() {
      const found = this.sortedChecks[0];
      return found ? found.severity : "ok";
    },
    coinSeverity() {
      if (!this.coins || this.coins.days_left === null) return "info";
      if (this.coins.days_left <= 3) return "error";
      if (this.coins.days_left <= 10) return "warning";
      return "info";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.isLoading = true;
      axios
        .get("/school-readiness/all")
        .then((response) => {
          this.checks = response.data.checks || [];
          this.coins = response.data.coins || null;
          this.generatedAt = response.data.generated_at || null;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_readiness"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    open(key, item) {
      const destination = DESTINATIONS[key];
      if (!destination || !item.id) return;
      this.$router.push(destination(item));
    },
    colorFor(severity) {
      return { error: "error", warning: "warning", info: "info", ok: "success" }[severity] || "info";
    },
    iconFor(severity) {
      return {
        error: "mdi-alert-circle",
        warning: "mdi-alert",
        info: "mdi-information",
        ok: "mdi-check-circle",
      }[severity] || "mdi-information";
    },
  },
};
</script>

<style scoped>
.readiness-card--error {
  border-left: 4px solid var(--v-error-base);
}
.readiness-card--warning {
  border-left: 4px solid var(--v-warning-base);
}
</style>
